/**
 * Sites — the physical place, maintained once, between a client and its jobs.
 *
 * Two tabs: the site list, and the operator-reviewed backfill that proposes one
 * site per distinct address across existing jobs. The backfill writes NOTHING
 * until the operator confirms, and jobs left unattached keep working exactly as
 * they do now off their embedded address.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { sitesAPI, clientsAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { MapPin, Plus, Search, Pencil, Building2, Wand2, Archive, ArchiveRestore } from 'lucide-react';

const emptySite = {
  client_id: '', site_code: '', name: '', address1: '', address2: '', city: '',
  state: '', zip_code: '', attention: '', contact_first_name: '', contact_last_name: '',
  site_phones: [], directions: '', keys_instructions: '', notes: '', is_active: true,
};

function SiteDialog({ open, onOpenChange, site, clients, onSaved }) {
  const [form, setForm] = useState({ ...emptySite });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(site ? { ...emptySite, ...site, site_phones: site.site_phones || [] } : { ...emptySite });
  }, [site, open]);
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.client_id) { toast.error('Pick a client'); return; }
    if (!form.name.trim()) { toast.error('The site needs a name'); return; }
    setSaving(true);
    try {
      if (site?.id) await sitesAPI.update(site.id, form);
      else await sitesAPI.create(form);
      toast.success(site?.id ? 'Site saved' : 'Site created');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="site-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-500" />
            {site?.id ? `Site — ${site.name}` : 'New site'}
          </DialogTitle>
          <DialogDescription>
            One address, however many jobs sit on it. Jobs attached to a site read their
            address from here.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Client</Label>
            <Select value={form.client_id || ''} onValueChange={(v) => change('client_id', v)}>
              <SelectTrigger data-testid="site-client"><SelectValue placeholder="Select a client…" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Site code</Label>
            <Input value={form.site_code} onChange={(e) => change('site_code', e.target.value.toUpperCase())}
              placeholder="Derived from the name if blank" data-testid="site-code" />
          </div>
          <div className="md:col-span-2">
            <Label>Site name</Label>
            <Input value={form.name} onChange={(e) => change('name', e.target.value)} data-testid="site-name" />
          </div>
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Input value={form.address1} onChange={(e) => change('address1', e.target.value)}
              placeholder="Street address" data-testid="site-address1" />
          </div>
          <div className="md:col-span-2">
            <Input value={form.address2} onChange={(e) => change('address2', e.target.value)}
              placeholder="Suite, floor, building (optional)" data-testid="site-address2" />
          </div>
          <div><Label>City</Label>
            <Input value={form.city} onChange={(e) => change('city', e.target.value)} data-testid="site-city" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>State</Label>
              <Input value={form.state} onChange={(e) => change('state', e.target.value)} data-testid="site-state" /></div>
            <div><Label>ZIP</Label>
              <Input value={form.zip_code} onChange={(e) => change('zip_code', e.target.value)} data-testid="site-zip" /></div>
          </div>
          <div><Label>Attention</Label>
            <Input value={form.attention} onChange={(e) => change('attention', e.target.value)} data-testid="site-attention" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact first</Label>
              <Input value={form.contact_first_name} onChange={(e) => change('contact_first_name', e.target.value)} data-testid="site-contact-first" /></div>
            <div><Label>Contact last</Label>
              <Input value={form.contact_last_name} onChange={(e) => change('contact_last_name', e.target.value)} data-testid="site-contact-last" /></div>
          </div>
          <div className="md:col-span-2"><Label>Directions</Label>
            <Input value={form.directions} onChange={(e) => change('directions', e.target.value)} data-testid="site-directions" /></div>
          <div className="md:col-span-2"><Label>Keys / access instructions</Label>
            <Input value={form.keys_instructions} onChange={(e) => change('keys_instructions', e.target.value)} data-testid="site-keys" /></div>
          <div className="md:col-span-2"><Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => change('notes', e.target.value)} data-testid="site-notes" /></div>
          <div className="flex items-center gap-2">
            <Switch checked={!!form.is_active} onCheckedChange={(v) => change('is_active', v)} data-testid="site-active" />
            <Label>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="site-save">
            {saving ? 'Saving…' : 'Save site'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackfillTab({ canEdit, onApplied }) {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [extras, setExtras] = useState({ skipped: [], needs_review: [] });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await sitesAPI.backfillPreview(null);
      setRows(data.rows);
      setSummary(data.summary);
      setPreviewId(data.preview_id);
      setExtras({ skipped: data.skipped, needs_review: data.needs_review });
      setSelected(Object.fromEntries(data.rows.map(r => [r.address_key, true])));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the review');
    } finally { setBusy(false); }
  };

  const editRow = (key, field, value) => setRows(rs => rs.map(
    r => (r.address_key === key ? { ...r, [field]: value } : r)));

  const confirm = async () => {
    const chosen = (rows || []).filter(r => selected[r.address_key]);
    if (!chosen.length) { toast.error('Nothing selected'); return; }
    setBusy(true);
    try {
      const { data } = await sitesAPI.backfillConfirm({
        preview_id: previewId,
        rows: chosen.map(r => ({
          address_key: r.address_key, name: r.name,
          site_code: r.site_code, job_ids: r.job_ids,
        })),
      });
      toast.success(`${data.sites_created} site(s) created, ${data.jobs_attached} job(s) attached`);
      setRows(null); setSummary(null); setPreviewId(null);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="site-backfill">
      <Card><CardContent className="p-5 space-y-3">
        <p className="text-sm text-fg-muted max-w-3xl">
          Groups existing jobs by address and proposes one site each. Several jobs at one
          building — physical security, cyber and SOC under different companies — land on a
          single site. Review the names and codes before you confirm; nothing is written until
          you do.
        </p>
        <Button onClick={run} disabled={busy || !canEdit} data-testid="backfill-run">
          <Wand2 className="w-4 h-4 mr-2" />{busy ? 'Working…' : 'Review proposed sites'}
        </Button>
      </CardContent></Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="backfill-summary">
          {[['New sites', summary.will_create], ['Existing matched', summary.will_update],
            ['Jobs to attach', summary.jobs_to_attach], ['Shared addresses', summary.multi_job_sites],
            ['Needs review', summary.needs_review]].map(([label, value]) => (
            <Card key={label}><CardContent className="p-4">
              <p className="text-xs text-fg-muted">{label}</p>
              <p className="text-2xl font-semibold">{value}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      {rows && (
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Site name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Jobs</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.address_key} data-testid={`backfill-row-${r.site_code}`}>
                  <TableCell>
                    <input type="checkbox" checked={!!selected[r.address_key]}
                      onChange={(e) => setSelected(s => ({ ...s, [r.address_key]: e.target.checked }))}
                      data-testid={`backfill-pick-${r.site_code}`} />
                  </TableCell>
                  <TableCell>
                    <Input className="h-8 min-w-[200px]" value={r.name}
                      onChange={(e) => editRow(r.address_key, 'name', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <Input className="h-8 w-28" value={r.site_code}
                      onChange={(e) => editRow(r.address_key, 'site_code', e.target.value.toUpperCase())} />
                  </TableCell>
                  <TableCell className="text-sm text-fg-muted">
                    {r.address1}{r.city ? `, ${r.city}` : ''} {r.state} {r.zip_code}
                  </TableCell>
                  <TableCell className="text-sm">{r.client_name || '—'}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex flex-wrap gap-1">
                      {r.jobs.map(j => (
                        <Badge key={j.id} variant="secondary" title={j.job_description || ''}>
                          {j.job_number}{j.company ? ` · ${j.company}` : ''}
                        </Badge>
                      ))}
                    </div>
                    {r.existing_site_id && (
                      <span className="text-xs text-amber-600">Attaches to an existing site</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="p-4 flex justify-end">
            <Button onClick={confirm} disabled={busy} data-testid="backfill-confirm">
              Create the selected sites
            </Button>
          </div>
        </CardContent></Card>
      )}

      {rows && !!extras.needs_review.length && (
        <Card><CardContent className="p-5">
          <p className="text-sm font-medium mb-2">Left alone — no street address on the job</p>
          <div className="flex flex-wrap gap-1" data-testid="backfill-needs-review">
            {extras.needs_review.map(j => (
              <Badge key={j.id} variant="outline">{j.job_number}</Badge>
            ))}
          </div>
          <p className="text-xs text-fg-muted mt-2">
            These keep working exactly as they do now. A roving job with no fixed address is
            valid and does not need a site.
          </p>
        </CardContent></Card>
      )}
    </div>
  );
}

export default function Sites() {
  const canAdd = useCap('clients', 'add');
  const canEdit = useCap('clients', 'edit');
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([sitesAPI.list({}), clientsAPI.list({ is_active: true })]);
      setSites(s.data);
      setClients(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sites');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => sites.filter(s => {
    if (clientFilter !== 'all' && s.client_id !== clientFilter) return false;
    if (!q.trim()) return true;
    const hay = `${s.name} ${s.site_code} ${s.address1} ${s.city} ${s.client_name}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  }), [sites, q, clientFilter]);

  const toggleActive = async (site) => {
    try {
      await sitesAPI.setActive(site.id, !site.is_active);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  return (
    <div className="space-y-6" data-testid="sites-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Sites</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            The place, kept once. A job can point at a site, cover several sites on a route,
            or carry its own address as it always has.
          </p>
        </div>
        {canAdd && (
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="sites-create-btn">
            <Plus className="w-4 h-4 mr-2" />New site
          </Button>
        )}
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="sites-tab-list">Sites</TabsTrigger>
          <TabsTrigger value="backfill" data-testid="sites-tab-backfill">Build from existing jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-4">
          <Card><CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-2.5 top-3 text-fg-muted" />
              <Input className="pl-8" placeholder="Search name, code, address…"
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="sites-search" />
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[260px]" data-testid="sites-client-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-sm text-fg-muted p-6">Loading…</TableCell></TableRow>
                ) : !filtered.length ? (
                  <TableRow><TableCell colSpan={6} className="text-sm text-fg-muted p-6" data-testid="sites-empty">
                    No sites yet. Create one, or build them from existing jobs on the next tab.
                  </TableCell></TableRow>
                ) : filtered.map(s => (
                  <TableRow key={s.id} data-testid={`site-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-fg-muted" />{s.name}
                        {!s.is_active && <Badge variant="secondary">Inactive</Badge>}
                        {s.source === 'job_backfill' && <Badge variant="outline">from jobs</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.site_code}</TableCell>
                    <TableCell className="text-sm">{s.client_name || '—'}</TableCell>
                    <TableCell className="text-sm text-fg-muted">
                      {s.address1}{s.city ? `, ${s.city}` : ''} {s.state} {s.zip_code}
                    </TableCell>
                    <TableCell className="text-right">{s.job_count}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setDialogOpen(true); }}
                            data-testid={`site-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => toggleActive(s)}
                            data-testid={`site-toggle-${s.id}`}>
                            {s.is_active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="backfill" className="mt-4">
          <BackfillTab canEdit={canAdd} onApplied={load} />
        </TabsContent>
      </Tabs>

      <SiteDialog open={dialogOpen} onOpenChange={setDialogOpen} site={editing}
        clients={clients} onSaved={load} />
    </div>
  );
}
